// src/components/sales/pages/scheduled-messages/ScheduledMessagesView.jsx
import React, { useMemo } from "react";
import { Plus, RefreshCw, X, Eye, Ban } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "Pending", label: "Pending" },
  { value: "Scheduled", label: "Scheduled" },
  { value: "Queued", label: "Queued" },
  { value: "Sent", label: "Sent" },
  { value: "Delivered", label: "Delivered" },
  { value: "Failed", label: "Failed" },
  { value: "Canceled", label: "Canceled" },
];

const norm = (v) => String(v ?? "").trim().toLowerCase();
const CANCELABLE = new Set(["pending", "scheduled", "queued"]);

// ✅ Treat API datetime without TZ as UTC
function parseApiDate(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  const hasTz = /([zZ]|[+-]\d{2}:\d{2})$/.test(s);
  const d = new Date(hasTz ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatApiDate(iso) {
  const d = parseApiDate(iso);
  if (!d) return String(iso ?? "-");
  return d.toLocaleString(undefined, { timeZoneName: "short" });
}

export default function ScheduledMessagesView({
  loading,
  messages,
  clientMap,
  templateMap,

  statusFilter,
  setStatusFilter,
  onApplyFilter,
  onClearFilter,
  onRefresh,
  onCreateClick,
  onView,
  onCancel,

  message,
  onClearMessage,
}) {
  const rows = Array.isArray(messages) ? messages : [];

  const getRowId = (r) =>
    r?.id ?? r?.scheduledMessageId ?? r?.messageId ?? r?.scheduledMessagesId ?? null;

  const getClientLabel = (clientId) => {
    const c = clientMap?.get?.(String(clientId));
    if (!c) return String(clientId ?? "-");
    const name = `${c.firstName || ""} ${c.secondName || ""}`.trim();
    const company = c.companyName || "";
    return company ? `${company}${name ? ` — ${name}` : ""}` : name || String(clientId ?? "-");
  };

  const getTemplateLabel = (templateId) => {
    const t = templateMap?.get?.(String(templateId));
    if (!t) return String(templateId ?? "-");
    return t.templateName || t.name || String(templateId ?? "-");
  };

  const canCancelRow = (row) => CANCELABLE.has(norm(row?.status ?? row?.state));

  const headers = useMemo(
    () => ["Client", "Template", "Channel", "Scheduling", "When/Trigger", "Status", "Actions"],
    []
  );

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Scheduled Messages</h2>
          <p className="text-xs text-white/70">
            Create messages to send immediately, schedule by date/time, or trigger after events.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onCreateClick}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <Plus className="w-5 h-5" />
            New Message
          </button>

          <button
            type="button"
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Optional Message Banner */}
      {message?.text ? (
        <div
          className={[
            "mb-6 rounded-2xl px-4 py-3 text-sm border backdrop-blur-xl flex items-start justify-between gap-3",
            message.type === "error"
              ? "border-red-500/30 bg-red-500/10 text-red-200"
              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
          ].join(" ")}
        >
          <div className="whitespace-pre-wrap">{message.text}</div>
          {onClearMessage && (
            <button
              type="button"
              onClick={onClearMessage}
              className="shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : null}

      {/* Filters */}
      <div className="mb-5 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-xl p-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-end md:justify-between">
          <div className="min-w-[220px]">
            <label className="text-xs text-white/70">Status</label>
            <select
              className={inputCls + " mt-2"}
              value={statusFilter}
              onChange={(e) => setStatusFilter?.(e.target.value)}
              disabled={loading}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="text-black">
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onApplyFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white backdrop-blur-lg disabled:opacity-50"
            >
              Apply
            </button>

            <button
              type="button"
              onClick={onClearFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white backdrop-blur-lg disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl flex items-center justify-between">
          <h3 className="text-lg font-bold">Messages</h3>
          <span className="text-sm text-gray-200">Total: {rows.length}</span>
        </div>

        <div className="p-0">
          <table className="w-full table-fixed text-white">
            <thead className="bg-white/10">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[18%]">{headers[0]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[18%]">{headers[1]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[10%]">{headers[2]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[14%]">{headers[3]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[18%]">{headers[4]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[10%]">{headers[5]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[12%]">{headers[6]}</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {rows.map((r) => {
                const id = getRowId(r);
                const clientId = r?.clientId;
                const templateId = r?.templateId;

                const channel = r?.channelType ?? "-";
                const scheduling = r?.schedulingType ?? "-";

                const when =
                  r?.scheduledDate
                    ? formatApiDate(r.scheduledDate) // ✅ fixed
                    : r?.triggerType
                      ? String(r.triggerType)
                      : "-";

                const status = r?.status ?? r?.state ?? "-";
                const cancelable = canCancelRow(r);

                return (
                  <tr key={String(id)} className="hover:bg-white/10 transition">
                    <td className="px-4 py-3 text-right align-top">
                      <div className="min-w-0">
                        <div className="text-sm break-words">{getClientLabel(clientId)}</div>
                        <div className="text-xs text-white/60 truncate">#{clientId ?? "-"}</div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <div className="min-w-0">
                        <div className="text-sm break-words">{getTemplateLabel(templateId)}</div>
                        <div className="text-xs text-white/60 truncate">#{templateId ?? "-"}</div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20 inline-block max-w-full truncate">
                        {channel}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <span className="px-2 py-1 rounded text-xs bg-purple-500/15 text-purple-200 border border-white/15 inline-block max-w-full truncate">
                        {scheduling}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <div className="text-sm break-words">{when}</div>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20 inline-block max-w-full truncate">
                        {status}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <div className="flex flex-col gap-2 items-end">
                        <button
                          type="button"
                          onClick={() => onView?.(r)}
                          disabled={loading}
                          className="w-full px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-xs backdrop-blur-lg flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>

                        <button
                          type="button"
                          onClick={() => onCancel?.(r)}
                          disabled={loading || !cancelable}
                          className={[
                            "w-full px-3 py-1.5 rounded-lg text-xs backdrop-blur-lg flex items-center justify-center gap-1 disabled:opacity-50 border",
                            cancelable
                              ? "bg-red-500/20 hover:bg-red-500/30 border-red-400/30 text-red-100"
                              : "bg-white/5 border-white/10 text-white/50",
                          ].join(" ")}
                          title={cancelable ? "Cancel this message" : "Only Pending/Scheduled/Queued can be canceled"}
                        >
                          <Ban className="w-4 h-4" />
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && rows.length === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={7}>
                    No scheduled messages yet.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={7}>
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
